import React, { useState } from 'react';
import { Camera, ExternalLink } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Gallery = () => {
  const [heroRef, heroVisible] = useScrollAnimation(0.1);
  const [selectedImage, setSelectedImage] = useState(null);

  const galleryPhotos = [
    {
      id: "photo-1",
      url: "https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/oq9hbmvg_IMG_0129.jpeg",
      alt: "Families celebrating and dancing together",
      category: "celebration"
    },
    {
      id: "photo-2",
      url: "https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/6vz4e3sz_IMG_0126.jpeg",
      alt: "Group photo at Mama-Mam's Kitchen",
      category: "celebration"
    },
    {
      id: "photo-3",
      url: "https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/mor4v1kk_IMG_0125.jpeg",
      alt: "Dancing and celebrating",
      category: "celebration"
    },
    {
      id: "photo-4",
      url: "https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/1giozw59_IMG_0127.jpeg",
      alt: "Multi-generational family gathering",
      category: "family"
    },
    {
      id: "photo-5",
      url: "https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/b8jxf7ge_IMG_0115.jpeg",
      alt: "Guests enjoying the event",
      category: "family"
    },
    {
      id: "photo-6",
      url: "https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/u01buin4_IMG_0111.jpeg",
      alt: "Celebrating together",
      category: "celebration"
    },
    {
      id: "photo-7",
      url: "https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/o2nxb030_IMG_0107.jpeg",
      alt: "Warm moment at Mama-Mam's",
      category: "family"
    },
    {
      id: "photo-8",
      url: "https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/tqhnalfr_IMG_0101.jpeg",
      alt: "Community gathering",
      category: "gathering"
    },
    {
      id: "photo-9",
      url: "https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/jz2vaed1_IMG_0100.jpeg",
      alt: "Delicious buffet spread",
      category: "food"
    },
    {
      id: "photo-10",
      url: "https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/gqawsiad_IMG_0096.jpeg",
      alt: "Buffet labels and dishes",
      category: "food"
    },
    {
      id: "photo-11",
      url: "https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/bnor1795_IMG_0105.jpeg",
      alt: "Dining together",
      category: "gathering"
    },
    {
      id: "photo-12",
      url: "https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/5jlps656_IMG_0092.jpeg",
      alt: "Elegant venue setting",
      category: "venue"
    },
    {
      id: "photo-13",
      url: "https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/hxzyvphj_IMG_0090.jpeg",
      alt: "Beautiful table setting",
      category: "venue"
    },
    {
      id: "photo-14",
      url: "https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/gu4hfhmj_IMG_0103.jpeg",
      alt: "Event moments",
      category: "gathering"
    }
  ];

  const closeModal = () => setSelectedImage(null);

  return (
    <div className="min-h-screen bg-cream page-transition">
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative h-96 flex items-center justify-center text-white"
        style={{
          backgroundImage: "url('https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/oq9hbmvg_IMG_0129.jpeg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 image-overlay-luxury"></div>
        <div className={`relative z-10 text-center ${heroVisible ? 'animate-fade-in-up' : 'opacity-0'}`} data-testid="gallery-hero">
          <Camera className="w-16 h-16 mx-auto mb-4 text-gold" />
          <h1 className="text-4xl md:text-6xl font-bold text-shadow-luxury" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Photo Gallery</h1>
          <div className="w-24 h-1 bg-gold mx-auto mt-4"></div>
          <p className="text-2xl mt-6 italic">Memories from Our Sunday Gatherings</p>
        </div>
      </section>

      {/* Dropbox Link Section */}
      <section className="py-12 px-4 bg-gold text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>View More Event Photos</h2>
          <p className="text-lg mb-6 opacity-90">Access our complete photo collection on Dropbox</p>
          <a 
            href="https://www.dropbox.com/scl/fo/bis80i3o8hx63mou6c2bx/APff3FuGi3K20wapXxYopcE?rlkey=s38hahvez45fp9vrlehp9jx2f&e=2&st=dmcf1joa&dl=0"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-brown hover:bg-cream font-semibold px-8 py-4 rounded-full text-lg transition-all transform hover:scale-105 shadow-lg"
          >
            <ExternalLink className="w-5 h-5" />
            Event Photos on Dropbox
          </a>
        </div>
      </section>

      {/* Photo Gallery Grid */}
      <section className="py-20 px-4 bg-cream-dark">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="w-16 h-1 bg-gold mx-auto mb-6"></div>
            <h2 className="text-5xl font-bold text-brown mb-4" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Our Moments Together</h2>
            <p className="text-lg text-gray-600 italic">Capturing the love, laughter, and togetherness</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryPhotos.map((photo) => (
              <div 
                key={photo.id}
                className="relative overflow-hidden rounded-lg shadow-luxury cursor-pointer group image-zoom-container"
                onClick={() => setSelectedImage(photo)}
              >
                <img 
                  src={photo.url} 
                  alt={photo.alt}
                  className="w-full h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                  <p className="text-white p-4 text-lg font-semibold">{photo.alt}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div className="relative max-w-6xl max-h-full">
            <button
              onClick={closeModal}
              className="absolute -top-12 right-0 text-white text-4xl hover:text-gold transition-colors"
            >
              ×
            </button>
            <img 
              src={selectedImage.url} 
              alt={selectedImage.alt}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
            <p className="text-white text-center mt-4 text-xl">{selectedImage.alt}</p>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-20 px-4 bg-white text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-brown mb-6" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Create Your Own Memories</h2>
        <p className="text-xl mb-8 text-gray-600">Join us for our next Sunday gathering</p>
        <a 
          href="/reserve" 
          className="inline-block bg-gold hover:bg-brown text-white font-semibold px-10 py-4 rounded-full text-lg transition-all transform hover:scale-105 shadow-lg"
        >
          Reserve Your Table →
        </a>
      </section>
    </div>
  );
};

export default Gallery;
